<template>
  <Transition name="desktop-page-backdrop">
    <div
      v-if="activeDesktopMenu"
      class="desktop-page-backdrop"
      :class="isLightHeader ? 'desktop-page-backdrop-light' : 'desktop-page-backdrop-dark'"
      aria-hidden="true"
      @click="closeDesktopMenu"
    />
  </Transition>

  <header class="site-header fixed top-0 w-full z-50" :class="{ 'header-tucked': headerTucked }">
    <div
      class="header-surface relative"
      :class="[
        { 'bar-sticky': isScrolling },
        isLightHeader
          ? 'bg-white/95 text-slate-950 shadow-sm shadow-slate-950/5 backdrop-blur-xl dark:bg-slate-950 dark:text-white dark:shadow-none'
          : 'bg-slate-950 dark:bg-slate-950',
      ]"
    >
      <div
        class="border-b text-xs"
        :class="
          isLightHeader
            ? 'border-slate-200 bg-white/90 text-slate-600 dark:border-white/10 dark:bg-slate-950/95 dark:text-slate-300'
            : 'border-white/10 bg-slate-950/95 text-slate-300'
        "
      >
        <!-- Founding 100 offer (data/founding-offer.ts). The same 36px strip, so the header and
             every page offset stay as they are; after the deadline the client only hides it.
             The whole line is the link: on phones there is no room for a separate button. -->
        <div
          v-if="foundingOffer.enabled"
          data-founding-offer="hold"
          class="mx-auto flex h-9 max-w-6xl items-center justify-center px-4 sm:px-6"
          :class="{ invisible: foundingOffer.expired.value }"
          :aria-hidden="foundingOffer.expired.value ? 'true' : undefined"
        >
          <a
            :href="handleReferral(foundingSignupUrl('https://app.workspace369.com/signup'))"
            class="group flex min-w-0 items-center gap-3 self-stretch"
            :tabindex="foundingOffer.expired.value ? -1 : undefined"
            data-conversion-event="start_free_click"
            data-conversion-label="Founding offer banner"
            data-conversion-location="founding-banner"
          >
            <span class="min-w-0 truncate">
              <span class="header-desktop-inline hidden">{{ currentLocaleUi().foundingBannerFull }}</span>
              <span class="header-mobile-only">{{ currentLocaleUi().foundingBannerShort }}</span>
            </span>
            <span
              class="hidden shrink-0 font-semibold transition-colors group-hover:underline sm:inline"
              :class="isLightHeader ? 'text-cyan-700 group-hover:text-cyan-900 dark:text-cyan-200' : 'text-cyan-200 group-hover:text-white'"
              >{{ currentLocaleUi().foundingBannerCta }}</span
            >
            <svg
              class="h-3.5 w-3.5 shrink-0 sm:hidden"
              :class="isLightHeader ? 'text-cyan-700 dark:text-cyan-200' : 'text-cyan-200'"
              viewBox="0 0 16 16"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
              aria-hidden="true"
            >
              <path d="m6 3.5 4.5 4.5L6 12.5" />
            </svg>
          </a>
        </div>
        <!-- Banner links fill the strip's height (self-stretch), so the whole 36px line is the tap target, not the 23px of text. -->
        <!-- Free done-for-you setup (data/setup-offer.ts): the Founding strip's place
             after its deadline, the same 36px line, while the setup offer runs (Dec 31).
             It links to the offer on /pricing (#free-setup), not to sign-up: a trial link needs its
             TrialDisclosure line beside it (B1.1), and a 36px strip has no room for one. The link text
             shows from md: below that, long translations at 125% text would push out the date. -->
        <div
          v-else-if="showSetupStrip"
          data-setup-offer
          class="mx-auto flex h-9 max-w-6xl items-center justify-center px-4 sm:px-6"
        >
          <NuxtLink
            :to="`${localePath('/pricing')}#free-setup`"
            class="group flex min-w-0 items-center gap-3 self-stretch"
            data-conversion-event="pricing_click"
            data-conversion-label="Setup offer banner"
            data-conversion-location="setup-banner"
          >
            <span class="min-w-0 truncate">
              <span class="header-desktop-inline hidden">{{ currentLocaleUi().setupBannerFull }}</span>
              <span class="header-mobile-only">{{ currentLocaleUi().setupBannerShort }}</span>
            </span>
            <span
              class="hidden shrink-0 font-semibold transition-colors group-hover:underline md:inline"
              :class="isLightHeader ? 'text-cyan-700 group-hover:text-cyan-900' : 'text-cyan-200 group-hover:text-white'"
              >{{ currentLocaleUi().setupBannerCta }}</span
            >
            <svg
              class="h-3.5 w-3.5 shrink-0 md:hidden"
              :class="isLightHeader ? 'text-cyan-700' : 'text-cyan-200'"
              viewBox="0 0 16 16"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
              aria-hidden="true"
            >
              <path d="m6 3.5 4.5 4.5L6 12.5" />
            </svg>
          </NuxtLink>
        </div>
        <div v-else class="mx-auto flex h-9 max-w-6xl items-center justify-center gap-2 px-4 sm:px-6">
          <span class="hidden sm:inline">
            {{ currentLocaleUi().topBannerFull }}
          </span>
          <span class="sm:hidden">
            {{ currentLocaleUi().topBannerShort }}
          </span>
          <a
            :href="handleReferral('https://app.workspace369.com')"
            class="flex items-center self-stretch font-semibold transition-colors"
            :class="isLightHeader ? 'text-cyan-700 hover:text-cyan-900 dark:text-cyan-200' : 'text-cyan-200 hover:text-white'"
            data-conversion-event="open_app_click"
            data-conversion-label="Top banner open app"
            data-conversion-location="header-banner"
            >{{ currentLocaleUi().openApp }}</a
          >
        </div>
      </div>
      <div class="header-shell">
        <div class="relative z-30 flex items-center justify-between h-16 md:h-20">
          <!-- Site branding -->
          <div class="desktop-brand self-center sm:ml-0 ml-3 flex-1">
            <!-- Logo -->
            <nuxt-link
              id="logo"
              class="inline-flex items-center sm:mt-2 mt-3"
              :class="isLightHeader ? 'text-slate-950 dark:text-white' : 'text-white'"
              :to="localePath('/')"
              aria-label="Workspace369"
            >
              <Logo
                class="max-w-none h-[38px] w-[38px]"
                :class="isLightHeader ? 'fill-slate-950 dark:fill-white' : 'fill-white'"
              />
              <span
                class="desktop-brand-wordmark ml-2 hidden font-semibold"
                :class="isLightHeader ? 'text-slate-950 dark:text-white' : 'text-white'"
                >Workspace369</span
              >
            </nuxt-link>
          </div>

          <!-- Desktop navigation -->
          <nav
            ref="desktopNavigation"
            class="desktop-navigation header-desktop-only hidden"
            aria-label="Primary navigation"
            @mouseenter="cancelDesktopMenuClose"
            @mouseleave="scheduleDesktopMenuClose"
            @focusout="handleDesktopFocusOut"
          >
            <ul class="desktop-navigation-list">
              <li
                v-for="item in desktopNavigationItems()"
                :key="item.id"
                class="desktop-navigation-item"
                @mouseenter="item.groups ? openDesktopMenu(item.id) : closeDesktopMenu()"
              >
                <button
                  v-if="item.groups"
                  :id="`desktop-nav-${item.id}`"
                  class="desktop-navigation-trigger"
                  :class="[
                    desktopNavToneClass,
                    {
                      'desktop-navigation-trigger-open': activeDesktopMenuId === item.id,
                      'desktop-navigation-trigger-active': isDesktopNavActive(item),
                    },
                  ]"
                  type="button"
                  aria-haspopup="true"
                  :aria-controls="`desktop-menu-${item.id}`"
                  :aria-expanded="activeDesktopMenuId === item.id"
                  @click="toggleDesktopMenu(item.id, $event)"
                >
                  <span>{{ item.label }}</span>
                  <svg aria-hidden="true" viewBox="0 0 16 16">
                    <path d="m4.5 6 3.5 3.5L11.5 6" />
                  </svg>
                </button>
                <nuxt-link
                  v-else
                  class="desktop-navigation-trigger desktop-navigation-direct"
                  :class="[
                    desktopNavToneClass,
                    { 'desktop-navigation-trigger-active': isDesktopNavActive(item) },
                  ]"
                  :to="item.to"
                  @focus="closeDesktopMenu"
                >
                  {{ item.label }}
                </nuxt-link>
              </li>
            </ul>
          </nav>

          <!-- Desktop product actions -->
          <ul class="desktop-actions header-desktop-only hidden justify-end items-center flex-1">
            <li>
              <LanguageSelector :tone="isLightHeader ? 'light' : 'dark'" />
            </li>
            <li>
              <ThemeToggle :tone="isLightHeader ? 'light' : 'dark'" />
            </li>
            <li>
              <a
                class="desktop-action-button desktop-dashboard-button"
                :class="
                  isLightHeader
                    ? 'desktop-dashboard-button-light'
                    : 'desktop-dashboard-button-dark'
                "
                :href="handleReferral('https://app.workspace369.com/login')"
                rel="noopener noreferrer"
                data-conversion-event="sign_in_click"
                data-conversion-label="Header sign in"
                data-conversion-location="header-desktop"
                >{{ currentLocaleUi().signIn }}</a
              >
            </li>
            <li class="desktop-start-item">
              <a
                class="desktop-action-button desktop-start-button"
                :href="handleReferral('https://app.workspace369.com/signup')"
                rel="noopener noreferrer"
                data-conversion-event="start_free_click"
                data-conversion-label="Header start free"
                data-conversion-location="header-desktop"
                aria-describedby="header-trial-terms"
              >
                {{ currentLocaleUi().startFree }}
              </a>
              <!-- Trial terms under the header trial button (legal B1.1), short form: the full line can't fit here. -->
              <TrialDisclosure
                id="header-trial-terms"
                compact
                class="desktop-start-terms"
                :on-light="isLightHeader"
                :on-dark="!isLightHeader"
              />
            </li>
          </ul>

          <!-- Mobile menu -->
          <div class="header-mobile-only flex items-center ml-4">
            <LanguageSelector
              class="mr-1"
              mobile
              :tone="isLightHeader ? 'light' : 'dark'"
            />

            <ThemeToggle
              class="mr-1"
              :tone="isLightHeader ? 'light' : 'dark'"
            />

            <!-- Hamburger button -->
            <button
              class="hamburger"
              ref="hamburger"
              :class="{ active: mobileNavOpen }"
              @click.stop="toggleMobileNav"
              aria-controls="mobile-nav"
              :aria-expanded="mobileNavOpen"
              :aria-label="mobileNavOpen ? 'Close navigation menu' : currentLocaleUi().menu"
            >
              <span class="sr-only">{{ currentLocaleUi().menu }}</span>
              <svg
                class="w-6 h-6 fill-current"
                :class="isLightHeader ? 'text-slate-700 dark:text-slate-300' : 'text-slate-300 dark:text-slate-300'"
                viewBox="0 0 24 24"
              >
                <rect class="hamburger-line hamburger-line-top" x="3" y="7" width="18" height="2" rx="1" />
                <rect class="hamburger-line hamburger-line-bottom" x="3" y="15" width="18" height="2" rx="1" />
              </svg>
            </button>

            <!-- Mobile navigation -->
            <Transition name="mobile-nav">
              <nav
                v-if="mobileNavOpen"
                id="mobile-nav"
                class="mobile-nav-panel"
                ref="mobileNav"
                :class="mobileNavToneClass"
                aria-label="Mobile navigation"
                tabindex="-1"
              >
                <div class="mobile-nav-shell">
                  <div class="mobile-nav-direct-list">
                    <template v-for="link in mobileDirectLinks()" :key="link.to">
                      <template v-if="link.id === 'features'">
                        <button
                          class="mobile-nav-direct-link mobile-nav-resource-trigger mobile-nav-features-trigger"
                          :class="{
                            'mobile-nav-direct-link-active': isMobileFeatureActive,
                            'mobile-nav-resource-trigger-open': mobileFeaturesOpen,
                          }"
                          type="button"
                          aria-controls="mobile-feature-links"
                          :aria-expanded="mobileFeaturesOpen"
                          :aria-current="isMobileFeatureActive ? 'true' : undefined"
                          @click="mobileFeaturesOpen = !mobileFeaturesOpen"
                        >
                          <span>{{ link.text }}</span>
                          <svg aria-hidden="true" viewBox="0 0 20 20">
                            <path d="m5.5 7.5 4.5 4.5 4.5-4.5" />
                          </svg>
                        </button>
                        <Transition name="mobile-resource-menu">
                          <div v-show="mobileFeaturesOpen" id="mobile-feature-links" class="mobile-nav-resource-menu mobile-nav-feature-menu">
                            <nuxt-link
                              class="mobile-nav-resource-link mobile-nav-feature-all"
                              :class="{ 'mobile-nav-resource-link-active': isFeaturesHubActive }"
                              :to="link.to"
                              :aria-current="isFeaturesHubActive ? 'page' : undefined"
                              @click="closeMobileNav"
                            >
                              <span>{{ featureMenu().overview.label }}</span>
                              <span class="dir-arrow" aria-hidden="true">→</span>
                            </nuxt-link>
                            <div
                              v-for="(group, groupIndex) in featureMenu().groups"
                              :key="group.title"
                              class="mobile-nav-feature-group"
                              role="group"
                              :aria-labelledby="`mobile-feature-group-${groupIndex}`"
                            >
                              <p :id="`mobile-feature-group-${groupIndex}`" class="mobile-nav-feature-label">{{ group.title }}</p>
                              <nuxt-link
                                v-for="item in group.links"
                                :key="item.to"
                                class="mobile-nav-resource-link"
                                :class="{ 'mobile-nav-resource-link-active': isActiveMobileLink(item.to) }"
                                :to="item.to"
                                :aria-current="isActiveMobileLink(item.to) ? 'page' : undefined"
                                @click="closeMobileNav"
                              >
                                <span :translate="item.brand ? 'no' : undefined">{{ item.label }}</span>
                                <span class="dir-arrow" aria-hidden="true">→</span>
                              </nuxt-link>
                            </div>
                          </div>
                        </Transition>
                      </template>
                      <nuxt-link
                        v-else
                        class="mobile-nav-direct-link"
                        :class="{ 'mobile-nav-direct-link-active': isActiveMobileLink(link.to) }"
                        :to="link.to"
                        :aria-current="isActiveMobileLink(link.to) ? 'page' : undefined"
                        @click="closeMobileNav"
                      >
                        <span>{{ link.text }}</span>
                        <span class="dir-arrow" aria-hidden="true">→</span>
                      </nuxt-link>
                    </template>
                    <button
                      class="mobile-nav-direct-link mobile-nav-resource-trigger"
                      :class="{
                        'mobile-nav-direct-link-active': isMobileResourceActive,
                        'mobile-nav-resource-trigger-open': mobileResourcesOpen,
                      }"
                      type="button"
                      aria-controls="mobile-resource-links"
                      :aria-expanded="mobileResourcesOpen"
                      @click="toggleMobileResources"
                    >
                      <span>{{ currentHeaderCopy().resources }}</span>
                      <svg aria-hidden="true" viewBox="0 0 20 20">
                        <path d="m5.5 7.5 4.5 4.5 4.5-4.5" />
                      </svg>
                    </button>
                    <Transition name="mobile-resource-menu">
                      <div
                        v-if="mobileResourcesOpen"
                        id="mobile-resource-links"
                        class="mobile-nav-resource-menu"
                      >
                        <template v-for="link in mobileResourceLinks" :key="link.to">
                          <a
                            v-if="link.external"
                            class="mobile-nav-resource-link"
                            :href="link.to"
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            <span>{{ link.text }}</span>
                            <span class="dir-arrow" aria-hidden="true">↗</span>
                          </a>
                          <nuxt-link
                            v-else
                            class="mobile-nav-resource-link"
                            :class="{ 'mobile-nav-resource-link-active': isActiveMobileLink(link.to) }"
                            :to="link.to"
                            @click="closeMobileNav"
                          >
                            <span>{{ link.text }}</span>
                            <span class="dir-arrow" aria-hidden="true">→</span>
                          </nuxt-link>
                        </template>
                      </div>
                    </Transition>
                    <a
                      class="mobile-nav-direct-link mobile-nav-direct-signin"
                      :href="handleReferral('https://app.workspace369.com/login')"
                      rel="noopener noreferrer"
                      data-conversion-event="sign_in_click"
                      data-conversion-label="Mobile menu sign in"
                      data-conversion-location="mobile-menu"
                    >
                      <span>{{ currentLocaleUi().signIn }}</span>
                      <span class="dir-arrow" aria-hidden="true">→</span>
                    </a>
                  </div>

                  <div class="mobile-nav-actions">
                    <a
                      class="mobile-nav-cta"
                      :href="handleReferral('https://app.workspace369.com/signup')"
                      rel="noopener noreferrer"
                      data-conversion-event="start_free_click"
                      data-conversion-label="Mobile menu start now"
                      data-conversion-location="mobile-menu"
                    >
                      <span>{{ currentLocaleUi().startFree }}</span>
                      <span class="dir-arrow" aria-hidden="true">→</span>
                    </a>
                    <nuxt-link
                      class="mobile-nav-contact"
                      :to="localePath('/contact-sales')"
                      data-conversion-event="contact_sales_click"
                      data-conversion-label="Mobile menu contact sales"
                      data-conversion-location="mobile-menu"
                      @click="closeMobileNav"
                    >
                      {{ tr("Contact Sales") }}
                    </nuxt-link>
                    <!-- Trial terms right under the trial button row, across both columns (legal B1.1), as on the sticky bars. -->
                    <TrialDisclosure
                      compact
                      class="mobile-nav-terms"
                      :on-light="mobileNavToneClass === 'mobile-nav-panel-light'"
                      :on-dark="mobileNavToneClass !== 'mobile-nav-panel-light'"
                    />
                  </div>
                </div>
              </nav>
            </Transition>
          </div>
        </div>
      </div>

      <Transition name="desktop-mega-menu">
        <div
          v-if="activeDesktopMenu"
          :id="`desktop-menu-${activeDesktopMenu.id}`"
          ref="desktopMegaPanel"
          class="desktop-mega-menu"
          :class="isLightHeader ? 'desktop-mega-menu-light' : 'desktop-mega-menu-dark'"
          :aria-labelledby="`desktop-nav-${activeDesktopMenu.id}`"
          @mouseenter="cancelDesktopMenuClose"
          @mouseleave="scheduleDesktopMenuClose"
          @focusout="handleDesktopFocusOut"
        >
          <div class="desktop-mega-menu-shell">
            <section class="desktop-mega-menu-intro">
              <p>{{ activeDesktopMenu.eyebrow }}</p>
              <h2>{{ activeDesktopMenu.title }}</h2>
              <p>{{ activeDesktopMenu.description }}</p>
              <nuxt-link
                v-if="activeDesktopMenu.overview"
                :to="activeDesktopMenu.overview.to"
                @click="closeDesktopMenu"
              >
                <span>{{ activeDesktopMenu.overview.label }}</span>
                <span class="dir-arrow" aria-hidden="true">→</span>
              </nuxt-link>
            </section>

            <div
              class="desktop-mega-menu-groups"
              :class="{ 'desktop-mega-menu-groups-four': activeDesktopMenu.groups.length > 3 }"
              :style="{ '--mega-cols': activeDesktopMenu.groups.length }"
            >
              <section
                v-for="group in activeDesktopMenu.groups"
                :key="group.title"
                class="desktop-mega-menu-group"
              >
                <h3>{{ group.title }}</h3>
                <div class="desktop-mega-menu-links">
                  <template v-for="link in group.links" :key="link.to">
                    <a
                      v-if="link.external"
                      :href="link.to"
                      target="_blank"
                      rel="noopener noreferrer"
                      @click="closeDesktopMenu"
                    >
                      <strong :translate="link.brand ? 'no' : undefined">{{ link.label }}</strong>
                      <small>{{ link.description }}</small>
                    </a>
                    <nuxt-link v-else :to="link.to" @click="closeDesktopMenu">
                      <strong :translate="link.brand ? 'no' : undefined">{{ link.label }}</strong>
                      <small>{{ link.description }}</small>
                    </nuxt-link>
                  </template>
                </div>
              </section>
            </div>
          </div>
        </div>
      </Transition>
    </div>
  </header>
</template>

<script>
// Same width as the CSS switch between the phone menu and the desktop header.
const DESKTOP_HEADER_QUERY = "(min-width: 64em)";
import { useReferral } from "../composables/useReferral";
import { getHeaderLocalizedCopy } from "../data/header-localized-copy";
import { localizedPath } from "../data/locales";
import { foundingSignupUrl } from "../data/founding-offer";
import { computed, nextTick, ref, watch, onMounted, onUnmounted } from "vue";

export default {
  name: "Header",
  setup() {
    const { addReferralToUrl } = useReferral();
    const { basePath, copyLocaleCode, currentLocaleCode, localeUi } = useWorkspaceLocale();
    const { tr } = useHomeI18n();
    const foundingOffer = useFoundingOffer();
    const showSetupStrip = useSetupStrip();
    const colorMode = useColorMode();
    const route = useRoute();
    const serverLocaleCode = import.meta.server ? currentLocaleCode.value : "";
    const headerLocaleCode = () => serverLocaleCode || currentLocaleCode.value;
    const localePath = (path) => localizedPath(path, headerLocaleCode());
    // Links follow the URL's locale; copy follows the rendered one, which is English
    // on generated editions (their page dictionary translates it).
    const currentHeaderCopy = () => getHeaderLocalizedCopy(copyLocaleCode.value);
    const currentLocaleUi = () => localeUi.value;

    const mobileDirectLinks = () => [
      { text: currentLocaleUi().nav.home, to: localePath("/") },
      { text: currentLocaleUi().nav.solutions, to: localePath("/solutions") },
      { id: "features", text: currentLocaleUi().nav.features, to: localePath("/features") },
      { text: currentLocaleUi().nav.buildWithAi, to: localePath("/build-a-crm-with-ai") },
      { text: currentLocaleUi().nav.pricing, to: localePath("/pricing") },
    ];

    const mobileResourceLinks = computed(() => {
      const links = currentHeaderCopy().resourcesMenu.links;
      return [
        { text: links.learn.label, to: localePath("/learn") },
        { text: links.research.label, to: localePath("/research") },
        { text: links.blog.label, to: localePath("/blog") },
        { text: links.docs.label, to: "https://docs.workspace369.com", external: true },
        { text: links.community.label, to: localePath("/community") },
        { text: links.customers.label, to: localePath("/customers") },
        { text: links.support.label, to: localePath("/support") },
        { text: links.roadmap.label, to: localePath("/roadmap") },
      ];
    });

    // The Features menu, shared by the desktop mega menu and the mobile disclosure. Its copy is
    // hand-translated per locale in data/header-localized-copy.ts (featuresMenu), so every
    // edition shows it in its own language. At most 4 groups: the mega grid has 4 columns.
    const featureMenu = () => {
      const copy = currentHeaderCopy().featuresMenu;
      // Product names stay in English in every locale (translate="no" keeps the page dictionaries off them).
      const brands = new Set(["flashPay", "voice"]);
      const link = (key, to) => ({ label: copy.links[key].label, description: copy.links[key].description, to: localePath(to), brand: brands.has(key) });
      return {
        id: "features",
        label: currentLocaleUi().nav.features,
        eyebrow: copy.eyebrow,
        title: copy.title,
        description: copy.description,
        overview: { label: copy.overview, to: localePath("/features") },
        groups: [
          {
            title: copy.groups.clientOperations,
            links: [
              link("crm", "/features/client-portal"),
              link("projects", "/features/schedule"),
              link("files", "/features/files"),
              link("access", "/features/client-scoped-access"),
            ],
          },
          {
            title: copy.groups.communication,
            links: [
              link("inbox", "/features/communications"),
              link("voice", "/features/workspace-voice"),
              link("ivr", "/features/auto-attendant-and-ivr"),
              link("postCall", "/features/post-call-automation"),
            ],
          },
          {
            title: copy.groups.revenue,
            links: [
              link("quotes", "/features/quoting"),
              link("invoices", "/features/invoices"),
              link("flashPay", "/features/flash-pay"),
              link("automation", "/features/automation"),
              link("analytics", "/features/analytics"),
            ],
          },
          {
            title: copy.groups.members,
            links: [
              link("memberships", "/features/memberships"),
              link("checkIns", "/features/check-ins"),
              link("programs", "/features/programs"),
            ],
          },
        ],
      };
    };

    // Solutions, Resources and Industries copy is hand-translated per locale (data/header-menus.json,
    // via currentHeaderCopy()), because these menus render client-side and miss the page dictionaries.
    const menuLink = (menu, key, to, external = false) => ({
      label: menu.links[key].label,
      description: menu.links[key].description,
      to: external ? to : localePath(to),
      ...(external ? { external: true } : {}),
    });

    const desktopNavigationItems = () => {
      const { solutionsMenu: solutions, resourcesMenu: resources, industriesMenu: industries } = currentHeaderCopy();
      return [
      featureMenu(),
      {
        id: "solutions",
        label: currentLocaleUi().nav.solutions,
        eyebrow: solutions.eyebrow,
        title: solutions.title,
        description: solutions.description,
        overview: { label: solutions.overview, to: localePath("/solutions") },
        groups: [
          {
            title: solutions.groups.connect,
            links: [
              menuLink(solutions, "clientOs", "/solutions/client-work-operating-system"),
              menuLink(solutions, "crm", "/solutions/workspace-crm"),
              menuLink(solutions, "stack", "/solutions/workspace-crm"),
              menuLink(solutions, "onboarding", "/solutions/client-onboarding-software"),
            ],
          },
          {
            title: solutions.groups.communicate,
            links: [
              menuLink(solutions, "phone", "/solutions/business-phone-system-with-crm"),
              menuLink(solutions, "inbox", "/solutions/shared-inbox-software"),
              menuLink(solutions, "sms", "/solutions/sms-inbox-for-client-work"),
              menuLink(solutions, "callSummaries", "/solutions/ai-call-summary-software"),
            ],
          },
          {
            title: solutions.groups.bill,
            links: [
              menuLink(solutions, "billing", "/solutions/professional-services-billing-software"),
              menuLink(solutions, "time", "/solutions/time-tracking-and-invoicing-software"),
              menuLink(solutions, "profitability", "/solutions/project-profitability-software-for-agencies"),
              menuLink(solutions, "portal", "/solutions/client-portal-and-billing-software"),
            ],
          },
        ],
      },
      {
        id: "resources",
        label: currentHeaderCopy().resources,
        eyebrow: resources.eyebrow,
        title: resources.title,
        description: resources.description,
        groups: [
          {
            title: resources.groups.learn,
            links: [
              menuLink(resources, "learn", "/learn"),
              menuLink(resources, "research", "/research"),
              menuLink(resources, "blog", "/blog"),
              menuLink(resources, "docs", "https://docs.workspace369.com", true),
            ],
          },
          {
            title: resources.groups.connect,
            links: [
              menuLink(resources, "community", "/community"),
              menuLink(resources, "customers", "/customers"),
            ],
          },
          {
            title: resources.groups.help,
            links: [
              menuLink(resources, "support", "/support"),
              menuLink(resources, "roadmap", "/roadmap"),
            ],
          },
        ],
      },
      {
        id: "industries",
        label: currentHeaderCopy().industries,
        eyebrow: industries.eyebrow,
        title: industries.title,
        description: industries.description,
        groups: [
          {
            title: industries.groups.agencies,
            links: [
              menuLink(industries, "agencies", "/solutions/agency-management-software"),
              menuLink(industries, "marketing", "/solutions/marketing-agency-project-management-software"),
              menuLink(industries, "creative", "/solutions/creative-agency-project-management-software"),
              menuLink(industries, "seo", "/solutions/seo-agency-management-software"),
            ],
          },
          {
            title: industries.groups.independents,
            links: [
              menuLink(industries, "consultants", "/solutions/invoicing-software-for-consultants"),
              menuLink(industries, "freelancers", "/solutions/freelancer-invoicing-software"),
            ],
          },
          {
            title: industries.groups.serviceTeams,
            links: [
              menuLink(industries, "professional", "/solutions/professional-services-billing-software"),
              menuLink(industries, "service", "/solutions/service-business-management-software"),
              menuLink(industries, "google", "/solutions/crm-for-google-workspace"),
            ],
          },
        ],
      },
      {
        id: "build-with-ai",
        label: currentLocaleUi().nav.buildWithAi,
        to: localePath("/build-a-crm-with-ai"),
      },
      {
        id: "pricing",
        label: currentLocaleUi().nav.pricing,
        to: localePath("/pricing"),
      },
      ];
    };

    // Path only: basePath keeps the query string, so an ad visit (/?utm_source=…) used to get the
    // dark inner-page header on the home page once it hydrated.
    const isHomePage = computed(() => (basePath.value.split(/[?#]/)[0] || "/") === "/");

    const handleReferral = (baseUrl) => {
      return addReferralToUrl(baseUrl);
    };

    const closeMobileNav = () => {
      mobileNavOpen.value = false;
    };

    const toggleMobileNav = () => {
      mobileNavOpen.value = !mobileNavOpen.value;
    };

    const toggleMobileResources = () => {
      mobileResourcesOpen.value = !mobileResourcesOpen.value;
    };

    const normalizePath = (path) => {
      if (!path || path === "/") return "/";
      return path.endsWith("/") ? path.slice(0, -1) : path;
    };

    const isActiveMobileLink = (to) => {
      const currentPath = normalizePath(route.path);
      const targetPath = normalizePath(to);
      if (targetPath === "/") return currentPath === "/";
      return currentPath === targetPath || currentPath.startsWith(`${targetPath}/`);
    };

    const activeDesktopMenuId = ref("");
    const mobileNavOpen = ref(false);
    const mobileResourcesOpen = ref(false);
    const mobileFeaturesOpen = ref(false);
    const desktopNavigation = ref(null);
    const desktopMegaPanel = ref(null);
    const lastDesktopMenuTrigger = ref(null);
    const mobileNav = ref(null);
    const hamburger = ref(null);
    const isScrolling = ref(false);
    const isMounted = ref(false);

    const isDarkTheme = computed(() => isMounted.value && colorMode.value === "dark");
    const isLightHeader = computed(() => isHomePage.value && !isDarkTheme.value);
    const mobileNavToneClass = computed(() =>
      isLightHeader.value && !isDarkTheme.value ? "mobile-nav-panel-light" : "mobile-nav-panel-dark",
    );
    const isMobileFeatureActive = computed(() => isActiveMobileLink(localePath("/features")));
    const isFeaturesHubActive = computed(() => normalizePath(route.path) === normalizePath(localePath("/features")));
    const isMobileResourceActive = computed(() =>
      mobileResourceLinks.value.some(
        (link) => !link.external && isActiveMobileLink(link.to),
      ),
    );

    const activeDesktopMenu = computed(() =>
      desktopNavigationItems().find((item) => item.id === activeDesktopMenuId.value),
    );

    const desktopNavToneClass = computed(() =>
      isLightHeader.value ? "desktop-navigation-trigger-light" : "desktop-navigation-trigger-dark",
    );

    const desktopItemPaths = (item) =>
      item.groups?.flatMap((group) => group.links.filter((link) => !link.external).map((link) => normalizePath(link.to))) || [];

    const isDesktopNavActive = (item) => {
      const currentPath = normalizePath(basePath.value);
      if (item.id === "pricing") return currentPath === "/pricing";
      if (item.id === "features") return currentPath === "/features" || currentPath.startsWith("/features/");
      if (item.id === "resources") return desktopItemPaths(item).some((path) => currentPath === path || currentPath.startsWith(`${path}/`));
      if (item.id === "industries") return desktopItemPaths(item).some((path) => currentPath === path);
      if (item.id === "solutions") {
        const industryItem = desktopNavigationItems().find((entry) => entry.id === "industries");
        const isIndustryPath = industryItem && desktopItemPaths(industryItem).some((path) => currentPath === path);
        return !isIndustryPath && (currentPath === "/solutions" || currentPath.startsWith("/solutions/"));
      }
      return false;
    };

    let desktopMenuCloseTimer;

    const cancelDesktopMenuClose = () => {
      if (!desktopMenuCloseTimer) return;
      window.clearTimeout(desktopMenuCloseTimer);
      desktopMenuCloseTimer = undefined;
    };

    const closeDesktopMenu = () => {
      cancelDesktopMenuClose();
      activeDesktopMenuId.value = "";
    };

    const scheduleDesktopMenuClose = () => {
      if (!import.meta.client) return;
      cancelDesktopMenuClose();
      desktopMenuCloseTimer = window.setTimeout(closeDesktopMenu, 140);
    };

    const openDesktopMenu = (menuId, event) => {
      cancelDesktopMenuClose();
      activeDesktopMenuId.value = menuId;
      if (event?.currentTarget) lastDesktopMenuTrigger.value = event.currentTarget;
    };

    const toggleDesktopMenu = (menuId, event) => {
      if (activeDesktopMenuId.value === menuId) {
        cancelDesktopMenuClose();
        if (event?.currentTarget) lastDesktopMenuTrigger.value = event.currentTarget;
        return;
      }
      openDesktopMenu(menuId, event);
    };

    const handleDesktopFocusOut = ({ relatedTarget }) => {
      if (
        desktopNavigation.value?.contains(relatedTarget) ||
        desktopMegaPanel.value?.contains(relatedTarget)
      ) return;
      scheduleDesktopMenuClose();
    };

    // On the home page on phones the 101px header (banner + nav) tucks away while you
    // scroll down and comes back on the first scroll up, so content gets the screen.
    const headerTucked = ref(false);
    const isHomeRoute = computed(() => /^\/(?:[a-z]{2}(?:-[a-z]{2,4})?\/?)?$/i.test(route.path));
    let lastScrollY = 0;

    const handleScroll = () => {
      const y = window.scrollY;
      isScrolling.value = y > 0;
      if (!isHomeRoute.value || mobileNavOpen.value || window.innerWidth >= 768) {
        headerTucked.value = false;
      } else if (y < 160) {
        headerTucked.value = false;
      } else if (y > lastScrollY + 6) {
        headerTucked.value = true;
      } else if (y < lastScrollY - 6) {
        headerTucked.value = false;
      }
      if (Math.abs(y - lastScrollY) > 6) lastScrollY = y;
    };

    // close on click outside
    const clickHandler = ({ target }) => {
      if (
        activeDesktopMenuId.value &&
        !desktopNavigation.value?.contains(target) &&
        !desktopMegaPanel.value?.contains(target)
      ) closeDesktopMenu();

      if (
        !mobileNavOpen.value ||
        mobileNav.value?.contains(target) ||
        hamburger.value?.contains(target)
      )
        return;
      closeMobileNav();
    };

    // close if the esc key is pressed
    const keyHandler = ({ key }) => {
      if (key !== "Escape") return;
      if (mobileNavOpen.value) {
        closeMobileNav();
        hamburger.value?.focus();
        return;
      }
      if (activeDesktopMenuId.value) {
        closeDesktopMenu();
        lastDesktopMenuTrigger.value?.focus();
      }
    };

    watch(
      () => route.fullPath,
      () => {
        closeMobileNav();
        closeDesktopMenu();
      },
    );

    watch(mobileNavOpen, async (isOpen) => {
      if (!import.meta.client) return;
      document.body.style.overflow = isOpen ? "hidden" : "";
      if (isOpen) {
        mobileResourcesOpen.value = isMobileResourceActive.value;
        mobileFeaturesOpen.value = false;
        await nextTick();
        mobileNav.value?.focus();
      }
    });

    watch(isHomeRoute, () => {
      headerTucked.value = false;
    });

    let desktopHeaderQuery;
    const handleDesktopHeaderChange = ({ matches }) => {
      if (matches) closeMobileNav();
    };

    onMounted(() => {
      isMounted.value = true;
      document.addEventListener("click", clickHandler);
      document.addEventListener("keydown", keyHandler);
      window.addEventListener("scroll", handleScroll, { passive: true });
      if (typeof window.matchMedia === "function") {
        desktopHeaderQuery = window.matchMedia(DESKTOP_HEADER_QUERY);
        desktopHeaderQuery.addEventListener("change", handleDesktopHeaderChange);
      }
    });

    onUnmounted(() => {
      cancelDesktopMenuClose();
      document.body.style.overflow = "";
      document.removeEventListener("click", clickHandler);
      document.removeEventListener("keydown", keyHandler);
      window.removeEventListener("scroll", handleScroll);
      desktopHeaderQuery?.removeEventListener("change", handleDesktopHeaderChange);
    });

    return {
      tr,
      headerTucked,
      activeDesktopMenuId,
      activeDesktopMenu,
      desktopNavigation,
      desktopMegaPanel,
      desktopNavigationItems,
      desktopNavToneClass,
      isDesktopNavActive,
      openDesktopMenu,
      closeDesktopMenu,
      toggleDesktopMenu,
      scheduleDesktopMenuClose,
      cancelDesktopMenuClose,
      handleDesktopFocusOut,
      mobileNavOpen,
      mobileNav,
      hamburger,
      isScrolling,
      handleReferral,
      closeMobileNav,
      toggleMobileNav,
      toggleMobileResources,
      mobileDirectLinks,
      mobileResourceLinks,
      mobileResourcesOpen,
      mobileFeaturesOpen,
      isMobileResourceActive,
      isMobileFeatureActive,
      isFeaturesHubActive,
      featureMenu,
      isActiveMobileLink,
      mobileNavToneClass,
      isHomePage,
      isLightHeader,
      localePath,
      currentHeaderCopy,
      currentLocaleUi,
      foundingOffer,
      foundingSignupUrl,
      showSetupStrip,
    };
  },
};
</script>

<style scoped>
.site-header {
  transition: transform 0.3s cubic-bezier(0.2, 0.8, 0.2, 1);
}

.site-header.header-tucked {
  transform: translateY(-100%);
}

@media (prefers-reduced-motion: reduce) {
  .site-header {
    transition: none;
  }
}

.bar-sticky::before {
  content: "";
  position: absolute;
  inset: 0px;
  bottom: -20px;
  -webkit-backdrop-filter: blur(0px);
  backdrop-filter: blur(0px);
  -webkit-mask: linear-gradient(to-b, black, transparent);
  mask: linear-gradient(to-b, black, transparent);
  -webkit-mask: linear-gradient(to bottom, black 60px, transparent);
  transform: translate3d(0, 0, 0);
  pointer-events: none;
}

/* Add these new styles for the top banner */
.bg-slate-900 {
  background-color: rgb(15 23 42);
}

/* Tablet and desktop mega navigation */
.desktop-page-backdrop {
  position: fixed;
  inset: 0;
  z-index: 40;
  display: none;
  cursor: default;
  -webkit-backdrop-filter: blur(4px) saturate(0.94);
  backdrop-filter: blur(4px) saturate(0.94);
}

.desktop-page-backdrop-light {
  background: rgb(15 23 42 / 0.075);
}

.desktop-page-backdrop-dark {
  background: rgb(2 6 23 / 0.17);
}

.desktop-page-backdrop-enter-active,
.desktop-page-backdrop-leave-active {
  transition: opacity 180ms ease;
}

.desktop-page-backdrop-enter-from,
.desktop-page-backdrop-leave-to {
  opacity: 0;
}

.desktop-brand,
.desktop-actions {
  flex: 0 0 auto;
}

.desktop-navigation {
  min-width: 0;
  justify-content: center;
}

.desktop-navigation-list {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.15rem;
  min-width: 0;
  margin: 0;
  padding: 0 0.35rem;
  list-style: none;
}

.desktop-navigation-item {
  display: flex;
  align-items: center;
}

.desktop-navigation-trigger {
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.3rem;
  min-height: 2.5rem;
  border: 0;
  border-radius: 0.4rem;
  background: transparent;
  padding: 0 0.58rem;
  font-family: inherit;
  font-size: 0.78rem;
  font-weight: 650;
  line-height: 1;
  text-decoration: none;
  white-space: nowrap;
  cursor: pointer;
  transition:
    background 150ms ease,
    color 150ms ease;
}

.desktop-navigation-trigger::after {
  content: "";
  position: absolute;
  right: 0.58rem;
  bottom: 0.22rem;
  left: 0.58rem;
  height: 2px;
  background: #1ba7ce;
  opacity: 0;
  transform: scaleX(0.35);
  transition:
    opacity 150ms ease,
    transform 180ms ease;
}

.desktop-navigation-trigger svg {
  width: 0.78rem;
  height: 0.78rem;
  fill: none;
  stroke: currentColor;
  stroke-linecap: round;
  stroke-linejoin: round;
  stroke-width: 1.75;
  transition: transform 160ms ease;
}

.desktop-navigation-trigger-light {
  color: #475569;
}

.desktop-navigation-trigger-dark {
  color: #cbd5e1;
}

.desktop-navigation-trigger-light:hover,
.desktop-navigation-trigger-light:focus-visible,
.desktop-navigation-trigger-light.desktop-navigation-trigger-open {
  background: rgb(15 23 42 / 0.055);
  color: #020617;
}

.desktop-navigation-trigger-dark:hover,
.desktop-navigation-trigger-dark:focus-visible,
.desktop-navigation-trigger-dark.desktop-navigation-trigger-open {
  background: rgb(255 255 255 / 0.07);
  color: #ffffff;
}

.desktop-navigation-trigger-open svg {
  transform: rotate(180deg);
}

.desktop-navigation-trigger-active::after,
.desktop-navigation-trigger-open::after {
  opacity: 1;
  transform: scaleX(1);
}

.desktop-navigation-trigger:focus-visible {
  outline: 2px solid #1ba7ce;
  outline-offset: 2px;
}

.desktop-navigation-direct {
  padding-inline: 0.72rem;
}

.desktop-actions > li {
  margin-left: 0.35rem;
}

.desktop-action-button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 5.75rem;
  min-height: 2.5rem;
  border: 1px solid transparent;
  border-radius: 0.45rem;
  padding: 0 0.8rem;
  font-size: 0.78rem;
  font-weight: 720;
  line-height: 1;
  text-decoration: none;
  white-space: nowrap;
  transition:
    background 150ms ease,
    border-color 150ms ease,
    color 150ms ease,
    transform 150ms ease;
}

.desktop-action-button:hover {
  transform: translateY(-1px);
}

.desktop-action-button:focus-visible {
  outline: 2px solid #1ba7ce;
  outline-offset: 2px;
}

.desktop-dashboard-button-light {
  border-color: #0f172a;
  background: #0f172a;
  color: #ffffff;
}

.desktop-dashboard-button-light:hover {
  border-color: #1e293b;
  background: #1e293b;
}

/* The home page server-renders its light header (the theme is only known in the browser), while
   the color-mode script sets html.dark before the first paint; these keep a dark-theme visit dark
   until hydration swaps in the -dark classes. */
:global(.dark .desktop-navigation-trigger-light) {
  color: #cbd5e1;
}

:global(.dark .desktop-dashboard-button-light) {
  border-color: #334155;
  background: #172033;
  color: #f8fafc;
}

.desktop-dashboard-button-dark {
  border-color: #334155;
  background: #172033;
  color: #f8fafc;
}

.desktop-dashboard-button-dark:hover {
  border-color: #475569;
  background: #243146;
}

.desktop-start-button {
  background: #0e7490;
  color: #ffffff;
  box-shadow: 0 8px 20px rgb(27 167 206 / 0.18);
}

.desktop-start-button:hover {
  background: #155e75;
}

/* The terms hang under the button inside the header's own height, so the header doesn't grow (CLS 0).
   TrialDisclosure has two root nodes and never gets this component's scoped attribute: use :deep. */
.desktop-start-item {
  position: relative;
}

.desktop-start-item :deep(.desktop-start-terms) {
  position: absolute;
  top: calc(100% + 2px);
  inset-inline-end: 0;
  margin: 0;
  max-width: none;
  white-space: nowrap;
  font-size: 0.75rem;
  line-height: 1rem;
  text-align: end;
  pointer-events: none;
}

.desktop-mega-menu {
  --mega-page: #ffffff;
  --mega-intro: #f5f8fb;
  --mega-text: #0f172a;
  --mega-muted: #64748b;
  --mega-line: rgb(15 23 42 / 0.11);
  --mega-hover: #edf7fa;
  position: absolute;
  top: 100%;
  right: 0;
  left: 0;
  z-index: 20;
  max-height: calc(100dvh - 7.25rem);
  overflow: hidden auto;
  border-top: 1px solid var(--mega-line);
  border-bottom: 1px solid var(--mega-line);
  background: var(--mega-page);
  color: var(--mega-text);
  box-shadow: 0 26px 64px rgb(15 23 42 / 0.16);
}

.desktop-mega-menu-dark {
  --mega-page: #050c1b;
  --mega-intro: #09162a;
  --mega-text: #f8fafc;
  --mega-muted: #94a3b8;
  --mega-line: rgb(148 163 184 / 0.2);
  --mega-hover: #0b2035;
  box-shadow: 0 30px 72px rgb(2 6 23 / 0.56);
}

.desktop-mega-menu-shell {
  display: grid;
  grid-template-columns: minmax(13.5rem, 0.8fr) minmax(0, 2.45fr);
  width: 100%;
  max-width: 80rem;
  margin: 0 auto;
}

/* Header bar container — same shell formula as the feature-page content
   (min(100% - 3rem, 84rem)) so logo, nav, and page content share edges. */
.header-shell {
  width: min(100% - 3rem, 84rem);
  margin: 0 auto;
}

.desktop-mega-menu-intro {
  min-width: 0;
  border-right: 1px solid var(--mega-line);
  background: var(--mega-intro);
  padding: 1.75rem 1.5rem 1.85rem;
}

.desktop-mega-menu-intro > p:first-child {
  margin: 0 0 0.72rem;
  color: #0e8eb3;
  font-size: 0.68rem;
  font-weight: 800;
  line-height: 1.3;
  text-transform: uppercase;
}

.desktop-mega-menu-dark .desktop-mega-menu-intro > p:first-child {
  color: #67e8f9;
}

.desktop-mega-menu-intro h2 {
  max-width: 17rem;
  margin: 0;
  color: var(--mega-text);
  font-size: clamp(1.18rem, 2vw, 1.55rem);
  font-weight: 750;
  line-height: 1.18;
}

.desktop-mega-menu-intro > p:nth-child(3) {
  max-width: 18rem;
  margin: 0.85rem 0 0;
  color: var(--mega-muted);
  font-size: 0.76rem;
  line-height: 1.65;
}

.desktop-mega-menu-intro > a {
  display: inline-flex;
  align-items: center;
  gap: 0.55rem;
  margin-top: 1.25rem;
  color: #0e8eb3;
  font-size: 0.75rem;
  font-weight: 750;
  text-decoration: none;
}

.desktop-mega-menu-dark .desktop-mega-menu-intro > a {
  color: #67e8f9;
}

.desktop-mega-menu-intro > a span:last-child {
  transition: transform 150ms ease;
}

.desktop-mega-menu-intro > a:hover span:last-child {
  transform: translateX(0.18rem);
}

.desktop-mega-menu-groups {
  display: grid;
  grid-template-columns: repeat(var(--mega-cols, 3), minmax(0, 1fr));
  min-width: 0;
  padding: 1.75rem 1.35rem 1.55rem;
}

.desktop-mega-menu-group {
  min-width: 0;
  border-right: 1px solid var(--mega-line);
  padding: 0 1.15rem;
}

.desktop-mega-menu-group:last-child {
  border-right: 0;
}

.desktop-mega-menu-group h3 {
  margin: 0 0 0.6rem;
  border-bottom: 1px solid var(--mega-line);
  padding: 0 0.45rem 0.58rem;
  color: var(--mega-muted);
  font-size: 0.68rem;
  font-weight: 750;
  line-height: 1.3;
  text-transform: uppercase;
}

/* Direction arrows point the reading way: mirrored on right-to-left pages (ar, he). */
.dir-arrow {
  display: inline-block;
}
[dir="rtl"] .dir-arrow {
  transform: scaleX(-1);
}

/* Four narrow columns: group titles may wrap, so each reserves two lines and the links line up. */
@media (max-width: 79.99em) {
  .desktop-mega-menu-groups-four .desktop-mega-menu-group h3 {
    display: flex;
    align-items: flex-end;
    min-height: calc(2 * 1.3em + 0.58rem);
  }
}

.desktop-mega-menu-links {
  display: grid;
  gap: 0.08rem;
}

.desktop-mega-menu-links a {
  display: block;
  min-width: 0;
  border-radius: 0.35rem;
  padding: 0.58rem 0.45rem 0.62rem;
  color: var(--mega-text);
  text-decoration: none;
  transition:
    background 150ms ease,
    color 150ms ease;
}

.desktop-mega-menu-links strong,
.desktop-mega-menu-links small {
  display: block;
  min-width: 0;
  /* Long single words (German compounds) hyphenate or wrap inside narrow columns. */
  hyphens: auto;
  overflow-wrap: anywhere;
}

.desktop-mega-menu-links strong {
  font-size: 0.76rem;
  font-weight: 700;
  line-height: 1.35;
}

.desktop-mega-menu-links small {
  margin-top: 0.18rem;
  color: var(--mega-muted);
  font-size: 0.65rem;
  line-height: 1.45;
}

.desktop-mega-menu-links a:hover,
.desktop-mega-menu-links a:focus-visible {
  background: var(--mega-hover);
  color: #0e8eb3;
  outline: none;
}

.desktop-mega-menu-dark .desktop-mega-menu-links a:hover,
.desktop-mega-menu-dark .desktop-mega-menu-links a:focus-visible {
  color: #67e8f9;
}

.desktop-mega-menu-enter-active,
.desktop-mega-menu-leave-active {
  transform-origin: top center;
  transition:
    opacity 160ms ease,
    transform 210ms cubic-bezier(0.22, 1, 0.36, 1),
    clip-path 210ms cubic-bezier(0.22, 1, 0.36, 1);
}

.desktop-mega-menu-enter-from,
.desktop-mega-menu-leave-to {
  opacity: 0;
  transform: translateY(-0.45rem);
  clip-path: inset(0 0 100% 0);
}

@media (min-width: 64em) and (max-width: 79.99em) {
  .desktop-brand {
    width: 2.5rem;
  }

  .desktop-navigation-list {
    padding-inline: 0.15rem;
  }

  .desktop-navigation-trigger {
    padding-inline: 0.45rem;
    font-size: 0.72rem;
  }

  .desktop-actions :deep(.language-trigger-desktop span) {
    display: none;
  }

  .desktop-actions :deep(.language-trigger-desktop) {
    width: 2.5rem;
    padding-inline: 0;
  }

  .desktop-action-button {
    min-width: 5.15rem;
    min-height: 2.35rem;
    padding-inline: 0.55rem;
    font-size: 0.72rem;
  }

  .desktop-mega-menu-shell {
    grid-template-columns: minmax(12.5rem, 0.72fr) minmax(0, 2.5fr);
  }

  .desktop-mega-menu-intro {
    padding-inline: 1.25rem;
  }

  .desktop-mega-menu-groups {
    padding-inline: 0.65rem;
  }

  .desktop-mega-menu-group {
    padding-inline: 0.65rem;
  }
}

/* Mobile menu styles */
.hamburger {
  @apply p-2;
  position: relative;
}

/* 40px box, 44px tap area: the extra 2px sits in the gap beside it, so nothing moves. */
.hamburger::before {
  content: "";
  position: absolute;
  inset: -2px;
}

/* The 38px logo link gets the same invisible ring: a 44px tap area, nothing moves. */
#logo {
  position: relative;
}

#logo::before {
  content: "";
  position: absolute;
  top: 50%;
  left: 0;
  width: 44px;
  height: 44px;
  transform: translate(-3px, -50%);
}

.hamburger:focus-visible {
  outline: 2px solid #1ba7ce;
  outline-offset: 4px;
  border-radius: 0.55rem;
}

.mobile-nav-panel {
  position: fixed;
  inset: 6.25rem 0 0;
  z-index: 20;
  height: calc(100dvh - 6.25rem);
  overflow: hidden auto;
  overscroll-behavior: contain;
  border-top: 1px solid var(--mobile-nav-line);
  background: var(--mobile-nav-page);
  color: var(--mobile-nav-text);
  box-shadow: 0 28px 72px var(--mobile-nav-shadow);
  outline: none;
}

.mobile-nav-panel-dark {
  --mobile-nav-page: #020617;
  --mobile-nav-line: rgba(148, 163, 184, 0.2);
  --mobile-nav-soft-line: rgba(148, 163, 184, 0.13);
  --mobile-nav-hover: #0b1729;
  --mobile-nav-active: #0c2136;
  --mobile-nav-text: #f8fafc;
  --mobile-nav-muted: #94a3b8;
  --mobile-nav-shadow: rgba(2, 6, 23, 0.52);
}

.mobile-nav-panel-light {
  --mobile-nav-page: #ffffff;
  --mobile-nav-line: rgba(15, 23, 42, 0.13);
  --mobile-nav-soft-line: rgba(15, 23, 42, 0.08);
  --mobile-nav-hover: #f1f5f9;
  --mobile-nav-active: #ecfeff;
  --mobile-nav-text: #020617;
  --mobile-nav-muted: #475569;
  --mobile-nav-shadow: rgba(15, 23, 42, 0.12);
}

.mobile-nav-shell {
  width: 100%;
  max-width: 58rem;
  min-height: 100%;
  margin: 0 auto;
  padding: 1rem 1rem calc(1rem + env(safe-area-inset-bottom));
}

.mobile-nav-enter-active,
.mobile-nav-leave-active {
  transition:
    opacity 220ms ease,
    transform 320ms cubic-bezier(0.22, 1, 0.36, 1),
    clip-path 320ms cubic-bezier(0.22, 1, 0.36, 1);
}

.mobile-nav-enter-from,
.mobile-nav-leave-to {
  opacity: 0;
  transform: translateY(-0.75rem);
  clip-path: inset(0 0 100% 0);
}

.mobile-nav-enter-to,
.mobile-nav-leave-from {
  opacity: 1;
  transform: translateY(0);
  clip-path: inset(0 0 0 0);
}

.mobile-nav-direct-list {
  display: grid;
  border-top: 1px solid var(--mobile-nav-line);
  border-bottom: 1px solid var(--mobile-nav-line);
}

.mobile-nav-direct-link {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: space-between;
  min-height: 3.25rem;
  border-inline-start: 2px solid transparent;
  border-bottom: 1px solid var(--mobile-nav-soft-line);
  color: var(--mobile-nav-text);
  padding: 0 0.7rem;
  font-size: 0.9rem;
  font-weight: 750;
  text-decoration: none;
  transition:
    background 150ms ease,
    border-color 150ms ease,
    color 150ms ease;
}

.mobile-nav-direct-link:last-child {
  border-bottom: 0;
}

.mobile-nav-direct-link span:last-child {
  color: #1ba7ce;
  transition: transform 150ms ease;
}

.mobile-nav-direct-link:hover,
.mobile-nav-direct-link-active {
  border-inline-start-color: #1ba7ce;
  background: var(--mobile-nav-active);
}

.mobile-nav-direct-link:hover span:last-child {
  transform: translateX(0.16rem);
}

.mobile-nav-resource-trigger {
  width: 100%;
  cursor: pointer;
  font-family: inherit;
  text-align: left;
}

.mobile-nav-resource-trigger svg {
  width: 1rem;
  height: 1rem;
  fill: none;
  stroke: #1ba7ce;
  stroke-linecap: round;
  stroke-linejoin: round;
  stroke-width: 1.8;
  transition: transform 180ms ease;
}

.mobile-nav-resource-trigger-open svg {
  transform: rotate(180deg);
}

.mobile-nav-resource-menu {
  display: grid;
  border-bottom: 1px solid var(--mobile-nav-line);
  background: var(--mobile-nav-hover);
  padding: 0.25rem 0.7rem 0.45rem;
}

.mobile-nav-resource-link {
  display: flex;
  align-items: center;
  justify-content: space-between;
  min-height: 2.65rem;
  border-bottom: 1px solid var(--mobile-nav-soft-line);
  color: var(--mobile-nav-text);
  padding: 0 0.5rem;
  font-size: 0.78rem;
  font-weight: 650;
  text-decoration: none;
  transition:
    color 150ms ease,
    background 150ms ease;
}

.mobile-nav-resource-link:last-child {
  border-bottom: 0;
}

/* Features disclosure: the desktop menu's groups, labels only. */
.mobile-nav-feature-group {
  display: grid;
  min-width: 0;
}

.mobile-nav-feature-label {
  margin: 0;
  padding: 0.85rem 0.5rem 0.3rem;
  color: var(--mobile-nav-muted);
  font-size: 0.72rem;
  font-weight: 750;
  letter-spacing: 0.04em;
  text-transform: uppercase;
}

.mobile-nav-feature-all {
  border-bottom: 1px solid var(--mobile-nav-soft-line);
}

.mobile-nav-resource-link span:last-child {
  color: #1ba7ce;
}

.mobile-nav-resource-link:hover,
.mobile-nav-resource-link-active {
  background: var(--mobile-nav-active);
  color: #1ba7ce;
}

.mobile-resource-menu-enter-active,
.mobile-resource-menu-leave-active {
  transition:
    opacity 180ms ease,
    transform 180ms ease;
}

.mobile-resource-menu-enter-from,
.mobile-resource-menu-leave-to {
  opacity: 0;
  transform: translateY(-0.35rem);
}

.mobile-nav-actions {
  position: sticky;
  bottom: -1rem;
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0.55rem;
  margin: 0 -0.15rem -1rem;
  border-top: 1px solid var(--mobile-nav-line);
  background: var(--mobile-nav-page);
  padding: 0.78rem 0.15rem calc(1rem + env(safe-area-inset-bottom));
}

/* The terms take their own row under both buttons. TrialDisclosure has two root nodes, so it never gets this
   component's scoped attribute: reach it with :deep or the rule never matches. */
.mobile-nav-actions :deep(.mobile-nav-terms) {
  grid-column: 1 / -1;
  margin: 0;
  max-width: none;
  text-align: center;
}

.mobile-nav-cta,
.mobile-nav-contact {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 2.65rem;
  /* Long labels wrap (fr "Contacter le service commercial" takes 3 lines at 125% text): keep them off the border. */
  padding-block: 0.45rem;
  border-radius: 0.45rem;
  font-size: 0.82rem;
  font-weight: 750;
  line-height: 1.15;
  text-align: center;
  text-decoration: none;
}

.mobile-nav-cta {
  gap: 0.45rem;
  background: #0e7490;
  color: #ffffff;
  padding-inline: 1rem;
  box-shadow: 0 12px 28px rgba(27, 167, 206, 0.2);
}

.mobile-nav-cta:hover {
  background: #155e75;
  transform: translateY(-1px);
}

.mobile-nav-contact {
  border: 1px solid var(--mobile-nav-line);
  background: transparent;
  color: var(--mobile-nav-text);
  padding-inline: 0.8rem;
}

.mobile-nav-contact:hover {
  border-color: rgba(27, 167, 206, 0.56);
  background: var(--mobile-nav-hover);
}

.mobile-nav-direct-link:focus-visible,
.mobile-nav-resource-link:focus-visible,
.mobile-nav-cta:focus-visible,
.mobile-nav-contact:focus-visible {
  outline: 2px solid #1ba7ce;
  outline-offset: 3px;
}

@media (min-width: 640px) {
  .mobile-nav-shell {
    padding: 1.15rem 1.5rem calc(1.15rem + env(safe-area-inset-bottom));
  }

  .mobile-nav-direct-list {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .mobile-nav-direct-link:nth-child(odd) {
    border-right: 1px solid var(--mobile-nav-soft-line);
  }

  .mobile-nav-resource-trigger,
  .mobile-nav-resource-menu,
  .mobile-nav-direct-signin {
    grid-column: 1 / -1;
    border-right: 0;
  }

  .mobile-nav-resource-menu {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    column-gap: 0.7rem;
  }

  .mobile-nav-resource-link:nth-child(odd) {
    border-right: 1px solid var(--mobile-nav-soft-line);
  }

  .mobile-nav-feature-all {
    grid-column: 1 / -1;
  }

  .mobile-nav-feature-group .mobile-nav-resource-link {
    border-right: 0;
  }

  .mobile-nav-feature-group .mobile-nav-resource-link:last-child {
    border-bottom: 0;
  }

  .mobile-nav-resource-link:nth-last-child(-n + 2) {
    border-bottom: 0;
  }

  .mobile-nav-actions {
    grid-template-columns: repeat(2, minmax(0, 11rem));
    justify-content: end;
  }

  /* Buttons sit at the right from 640px, so the terms line up under them. */
  .mobile-nav-actions :deep(.mobile-nav-terms) {
    text-align: right;
  }
}

@media (min-width: 768px) {
  .mobile-nav-panel {
    inset: 7.25rem 0 0;
    height: calc(100dvh - 7.25rem);
  }
}

/* The desktop header needs 64em, not 1024px: with a larger browser text size the rem-sized
   nav outgrows a 1024px row and runs into the logo and the buttons, so it switches to the
   phone menu instead. At the default 16px text this is exactly 1024px. */
@media (min-width: 64em) {
  .header-desktop-only {
    display: flex;
  }

  .desktop-navigation.header-desktop-only {
    flex-grow: 1;
  }

  .header-desktop-inline {
    display: inline;
  }

  .header-mobile-only {
    display: none;
  }
}

/* The wordmark joins the logo once the full-width header fits (80em = 1280px at default text). */
@media (min-width: 80em) {
  .desktop-brand-wordmark {
    display: inline;
  }

  .desktop-page-backdrop {
    display: block;
  }

  .mobile-nav-shell {
    max-width: 62rem;
    padding-inline: 2rem;
  }
}

@media (prefers-reduced-motion: reduce) {
  .desktop-navigation-trigger,
  .desktop-navigation-trigger::after,
  .desktop-navigation-trigger svg,
  .desktop-action-button,
  .desktop-page-backdrop-enter-active,
  .desktop-page-backdrop-leave-active,
  .desktop-mega-menu-enter-active,
  .desktop-mega-menu-leave-active,
  .desktop-mega-menu-intro > a span:last-child,
  .mobile-nav-enter-active,
  .mobile-nav-leave-active,
  .mobile-resource-menu-enter-active,
  .mobile-resource-menu-leave-active,
  .mobile-nav-resource-trigger svg,
  .mobile-nav-cta,
  .mobile-nav-contact {
    transition-duration: 1ms;
  }
}
</style>
